/**
 * applyCajaDemoFreshStart.ts — Bootstrap demo de caja sin borrar datos del usuario.
 *
 * - Solo inicializa claves vacías en el primer arranque (versión persist-v3).
 * - Nunca limpia sesiones abiertas, ventas, creditos ni movimientos guardados.
 * - Un cierre de referencia solo si el historial de cierres está vacío.
 */

import { setStorage } from "@/mocks/helpers/storage";
import { txRead } from "@/lib/persistence/transactionalStore";
import {
  ensurePuntosDemo,
  getSesionesMapRaw,
  mirrorSesionLegacy,
  persistSesionesMap,
} from "@/mocks/helpers/cajaMulticaja";
import type { CierreCaja } from "../types/caja.types";
import { MAY12_DEMO_SEED_VERSION } from "./may12CajaDemoSeed.data";
import {
  buildMulticajaJornadaCierres,
  ensureMulticajaDemoPuntos,
} from "./multicajaJornadaDemo.data";
import {
  ANEXO_B_CIERRE_DEMO_ID,
  ANEXO_B_DEMO_VERSION,
  buildAnexoBCierreDemo,
} from "./anexoBCierreDemo.data";
import { DEMO_CIERRE_REF_IDS } from "./resolveCierreDemoData";
import {
  localCalendarDayIso,
  shiftCajaDemoAnchorDatesDeep,
} from "./shiftCajaDemoMay12AnchorToLocalDay";
import {
  ensureDemoHistorialSynced,
  demoShowcaseMissingFromHistorial,
  demoJornadaVentasMissingFromHistorial,
} from "./syncDemoHistorialFromCierres";
import { ensureDemoStockSyncedFromHistorial } from "./syncDemoStockMovementsFromVentas";
import {
  ensureDemoJornadaForToday,
  hasUserRealCajaActivity,
  purgeDemoJornadaFromStorage,
  LS_DEMO_CALENDAR_DAY,
} from "./demoJornadaLifecycle";

const LS_DEMO_FLAG = "altenot_caja_demo_fresh_applied";
const LS_CIERRE_SEED_VERSION = "altenot_caja_demo_cierre_seed";
const LS_ANEXO_B_DEMO_VERSION = "altenot_caja_demo_anexo_b_version";
/** Sin fecha: los datos del demo persisten entre días y recargas. */
const DEMO_SEED_VERSION = "persist-v4";

function buildDemoCierresForHistorial(targetDay: string): CierreCaja[] {
  return [
    ...shiftCajaDemoAnchorDatesDeep(buildMulticajaJornadaCierres(), targetDay),
    buildAnexoBCierreDemo(),
  ];
}

function readStorageArray(key: string): unknown[] {
  try {
    const parsed = txRead<unknown>(key, []);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function isCajaDemoFreshStartApplied(): boolean {
  try {
    return localStorage.getItem(LS_DEMO_FLAG) === DEMO_SEED_VERSION;
  } catch {
    return false;
  }
}

function seedEmptyStorageKey(key: string): void {
  if (localStorage.getItem(key) !== null) return;
  setStorage(key, []);
}

/** Reemplaza los cierres demo en historial cuando cambia MAY12_DEMO_SEED_VERSION. */
export function refreshDemoCierreIfStale(): boolean {
  try {
    const storedVersion = localStorage.getItem(LS_CIERRE_SEED_VERSION);
    const storedAnexoBVersion = localStorage.getItem(LS_ANEXO_B_DEMO_VERSION);
    const hasCierresKey =
      localStorage.getItem("caja_cierres_historial") !== null;
    const parsed = txRead<unknown[]>("caja_cierres_historial", []);
    const hasAnexoBCierre = Array.isArray(parsed)
      ? parsed.some(
          (c) =>
            typeof c === "object" &&
            c !== null &&
            (c as { id?: string }).id === ANEXO_B_CIERRE_DEMO_ID,
        )
      : false;
    if (
      storedVersion === MAY12_DEMO_SEED_VERSION &&
      storedAnexoBVersion === ANEXO_B_DEMO_VERSION &&
      hasAnexoBCierre
    ) {
      return false;
    }

    const targetDay = localCalendarDayIso();
    const freshJornada = buildDemoCierresForHistorial(targetDay);

    if (!hasCierresKey) {
      setStorage("caja_cierres_historial", freshJornada);
      localStorage.setItem(LS_CIERRE_SEED_VERSION, MAY12_DEMO_SEED_VERSION);
      localStorage.setItem(LS_ANEXO_B_DEMO_VERSION, ANEXO_B_DEMO_VERSION);
      try {
        localStorage.setItem(LS_DEMO_CALENDAR_DAY, targetDay);
      } catch {
        /* ignore */
      }
      ensureDemoHistorialSynced({ force: true });
      return true;
    }

    if (!Array.isArray(parsed)) {
      setStorage("caja_cierres_historial", freshJornada);
      localStorage.setItem(LS_CIERRE_SEED_VERSION, MAY12_DEMO_SEED_VERSION);
      localStorage.setItem(LS_ANEXO_B_DEMO_VERSION, ANEXO_B_DEMO_VERSION);
      try {
        localStorage.setItem(LS_DEMO_CALENDAR_DAY, targetDay);
      } catch {
        /* ignore */
      }
      ensureDemoHistorialSynced({ force: true });
      return true;
    }

    const next = [...(parsed as CierreCaja[])];
    let changed = false;
    for (const fresh of freshJornada) {
      const idx = next.findIndex((c) => c?.id === fresh.id);
      if (idx >= 0) {
        next[idx] = fresh;
        changed = true;
      } else if (
        DEMO_CIERRE_REF_IDS.includes(fresh.id) ||
        fresh.id === ANEXO_B_CIERRE_DEMO_ID
      ) {
        next.push(fresh);
        changed = true;
      }
    }

    if (changed) {
      setStorage("caja_cierres_historial", next);
    }
    localStorage.setItem(LS_CIERRE_SEED_VERSION, MAY12_DEMO_SEED_VERSION);
    localStorage.setItem(LS_ANEXO_B_DEMO_VERSION, ANEXO_B_DEMO_VERSION);
    try {
      localStorage.setItem(LS_DEMO_CALENDAR_DAY, localCalendarDayIso());
    } catch {
      /* ignore */
    }
    ensureDemoHistorialSynced({ force: true });
    return true;
  } catch {
    return false;
  }
}

/**
 * Reset completo solo con `{ force: true }` (herramienta de desarrollo).
 */
export function applyCajaDemoFreshStart(options?: { force?: boolean }): void {
  // GARANTÍA CRÍTICA: el catálogo de puntos de venta debe existir SIEMPRE, en
  // cada arranque, sin importar el flag de demo ni si hay actividad real. En
  // Android el WebView puede evictar `caja_puntos_catalogo` de localStorage
  // mientras el flag `altenot_caja_demo_fresh_applied` persiste o se restaura
  // parcialmente desde IndexedDB; sin esta línea la caja mostraría "Sin puntos"
  // y no se podría abrir. Es idempotente: solo siembra "Caja principal" si falta.
  ensurePuntosDemo();
  ensureMulticajaDemoPuntos();

  if (!options?.force && isCajaDemoFreshStartApplied()) {
    if (hasUserRealCajaActivity()) {
      purgeDemoJornadaFromStorage();
      return;
    }

    if (demoJornadaVentasMissingFromHistorial()) {
      try {
        localStorage.removeItem("altenot_caja_demo_historial_sync");
      } catch {
        /* ignore */
      }
    }
    // La jornada multicaja puede existir aunque una versión nueva agregue otro
    // cierre demo independiente. Aplica primero la migración idempotente.
    const refreshedCierres = refreshDemoCierreIfStale();
    const cierresActuales = readStorageArray("caja_cierres_historial");
    const hasDemos = (cierresActuales as { id?: string }[]).some(
      (c) => c?.id && DEMO_CIERRE_REF_IDS.includes(c.id),
    );
    if (refreshedCierres || cierresActuales.length === 0 || !hasDemos) {
      ensureDemoJornadaForToday();
      if (demoJornadaVentasMissingFromHistorial()) {
        ensureDemoJornadaForToday({ force: true });
      } else if (demoShowcaseMissingFromHistorial()) {
        ensureDemoHistorialSynced({ force: true });
      }
    } else {
      ensureDemoHistorialSynced();
    }
    ensureDemoStockSyncedFromHistorial();
    return;
  }

  ensurePuntosDemo();
  ensureMulticajaDemoPuntos();

  if (options?.force) {
    const targetDay = localCalendarDayIso();
    const cierresRef = buildDemoCierresForHistorial(targetDay);
    setStorage("caja_ventas_historial", []);
    setStorage("caja_movimientos", []);
    setStorage("caja_creditos", []);
    setStorage("caja_cierres_historial", cierresRef);
    ensureDemoHistorialSynced({ force: true });
    persistSesionesMap({});
    mirrorSesionLegacy(null, null);
    try {
      localStorage.removeItem("caja.pendingWhatsAppReport");
      localStorage.removeItem("altenot_caja_demo_may12_applied");
      localStorage.setItem("caja_last_status", "closed");
      localStorage.setItem(LS_DEMO_FLAG, DEMO_SEED_VERSION);
      localStorage.setItem(LS_CIERRE_SEED_VERSION, MAY12_DEMO_SEED_VERSION);
    } catch {
      /* ignore */
    }
    return;
  }

  seedEmptyStorageKey("caja_ventas_historial");
  seedEmptyStorageKey("caja_movimientos");
  seedEmptyStorageKey("caja_creditos");

  const cierres = readStorageArray("caja_cierres_historial");
  const hasDemoCierres = (cierres as CierreCaja[]).some((c) =>
    DEMO_CIERRE_REF_IDS.includes(c?.id),
  );
  if (cierres.length === 0 || !hasDemoCierres) {
    const targetDay = localCalendarDayIso();
    const cierresRef = buildDemoCierresForHistorial(targetDay);
    setStorage("caja_cierres_historial", cierresRef);
    ensureDemoHistorialSynced({ force: true });
  }

  const sesiones = getSesionesMapRaw();
  const hasOpenSession = Object.keys(sesiones).length > 0;
  try {
    if (
      !hasOpenSession &&
      localStorage.getItem("caja_last_status") !== "open"
    ) {
      localStorage.setItem("caja_last_status", "closed");
    }
    if (!hasUserRealCajaActivity()) {
      refreshDemoCierreIfStale();
      ensureDemoJornadaForToday({ force: true });
    } else {
      ensureDemoHistorialSynced();
    }
    localStorage.setItem(LS_DEMO_FLAG, DEMO_SEED_VERSION);
  } catch {
    /* ignore */
  }
}
